import { DragHandleIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  ListItem,
  Skeleton,
  Spacer,
  Stack,
  Step,
  StepIndicator,
  Stepper,
  StepSeparator,
  StepStatus,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  finleyAssert,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  useGetWorkflowTemplateConstructorQuery,
  useCreateWorkflowTemplateMutation,
  WorkflowTypeGQLType,
  WorkflowUserApprovalGraphNodeGQLType,
  WorkflowGraphEdgesGQLType,
  WorkflowActionGraphNodeGQLType,
  WorkflowStringArrayComparatorGQLType,
  WorkflowGraphObjectGQLType,
  useGetWorkflowTemplateQuery,
  useGetWorkflowTemplatesQuery,
  WorkflowTemplateConstructorGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { reorder } from 'packages/common-frontend/components/DragAndDropList';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { Select } from 'packages/common-frontend/components/Select';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

const constructWorkflowGraphEdges = (
  nodes: WorkflowUserApprovalGraphNodeGQLType[],
): WorkflowGraphEdgesGQLType[] => {
  const edges: WorkflowGraphEdgesGQLType[] = [];

  for (const [index, node] of nodes.entries()) {
    if (index < nodes.length - 1) {
      edges.push({ from: node.id, to: nodes[index + 1].id });
    }
  }
  return edges;
};

const _constructWorkflowGraphNodesListHelper = ({
  currentNode,
  edgesByFrom,
  nodesById,
}: {
  currentNode: WorkflowUserApprovalGraphNodeGQLType;
  edgesByFrom: { [key: string]: WorkflowGraphEdgesGQLType };
  nodesById: { [key: string]: WorkflowUserApprovalGraphNodeGQLType };
}): WorkflowUserApprovalGraphNodeGQLType[] => {
  const { __typename: _unused, ...currentNodeBody } = currentNode;
  const foundEdge: WorkflowGraphEdgesGQLType | undefined =
    edgesByFrom[currentNodeBody.id];

  if (!foundEdge) {
    return [currentNodeBody];
  }

  const nextNodeId = foundEdge.to;
  const nextNode = nodesById[nextNodeId];

  return [
    currentNodeBody,
    ..._constructWorkflowGraphNodesListHelper({
      currentNode: nextNode,
      edgesByFrom,
      nodesById,
    }),
  ];
};

const constructWorkflowGraphNodesList = (
  graph: WorkflowGraphObjectGQLType,
): WorkflowUserApprovalGraphNodeGQLType[] => {
  const nodesById = _.keyBy(graph.userApprovalNodes, (n) => n.id);

  const actionNodeIds = graph.actionNodes.map((a) => a.id);

  // sarah todo: address after jan 20 rollout
  const edgesWithoutFinalActionNode = graph.edges.filter(
    (e) => !actionNodeIds.includes(e.to),
  );

  // sarah todo: key by is only working here because the graphs are always a list for MVP, but once a node can have multiple outcoming edges
  // this will no longer apply... will need to top sort then
  const edgesByFrom = _.keyBy(edgesWithoutFinalActionNode, (e) => e.from);
  const edgesTo = new Set(edgesWithoutFinalActionNode.map((e) => e.to));

  const startingNode = graph.userApprovalNodes.filter(
    (n) => !edgesTo.has(n.id),
  );

  finleyAssert(
    startingNode.length === 1,
    `[Workflows] Could not find a single starting node for existing graph.`,
  );

  return _constructWorkflowGraphNodesListHelper({
    currentNode: startingNode[0],
    edgesByFrom,
    nodesById,
  });
};

const WorkflowUserApprovalGraphNode = ({
  node,
  approvalUserOptions,
  onChange,
  onDelete,
  isEditable,
  isDragging,
}: {
  node: WorkflowUserApprovalGraphNodeGQLType;
  approvalUserOptions: { label: string; value: string }[];
  onChange?: (newNode: WorkflowUserApprovalGraphNodeGQLType) => void;
  onDelete?: () => void;
  isEditable: boolean;
  isDragging: boolean;
}) => {
  return (
    <HStack
      padding="24px"
      borderRadius="8px"
      background={colors['v2-aurora'][50]}
      borderColor={
        isDragging ? colors['v2-aurora'][400] : colors['v2-aurora'][50]
      }
      borderWidth={isDragging ? '1px' : '0px'}
      spacing="20px"
    >
      {(isEditable || isDragging) && <DragHandleIcon />}
      <Box background="white" width="25%">
        <Select<WorkflowStringArrayComparatorGQLType>
          value={node.comparator}
          isDisabled={!isEditable}
          options={[
            {
              label: 'Require Any',
              value: 'CONTAINS_ANY_OF',
            },
            {
              label: 'Require All',
              value: 'CONTAINS_ALL_OF',
            },
          ]}
          onChange={(value) => {
            if (value && onChange) {
              onChange({ ...node, comparator: value });
            }
          }}
        />
      </Box>

      <Box background="white" minWidth="25%">
        <IsolatedMultiSelect
          applyButtonText="Save"
          buttonProps={{
            isDisabled: !isEditable,
            borderWidth: node.userIds.length === 0 ? '2px' : '1px',
            borderColor: node.userIds.length === 0 ? 'red' : 'inherit',
            width: 'full',
          }}
          counterLabel={approvalUserOptions
            .filter(({ value }) => node.userIds.includes(value))
            .map(({ label }) => label)
            .join(', ')}
          initialSelections={node.userIds}
          isSearchable
          placeholderText="Select users"
          onApplyOptions={(value) => {
            if (onChange) {
              onChange({ ...node, userIds: value });
            }
          }}
          options={approvalUserOptions}
          showSelectedCount
          titleText="All users"
        />
      </Box>

      <Spacer />
      {isEditable && onDelete && (
        <IconButtonV2
          icon={<Icon icon="trash-can" />}
          size="sm"
          aria-label=""
          variant="secondary"
          colorScheme="v2-aurora"
          onClick={() => {
            onDelete();
          }}
        />
      )}
    </HStack>
  );
};

// Exports
const WorkflowAuthorTemplate = ({
  workflowType,
  organizationId,
  placeholderName,
  heading,
  onCancel,
  onSave,
  startsEditable = false,
  existingWorkflowId,
  existingWorkflowGraph,
}: {
  workflowType: WorkflowTypeGQLType;
  organizationId: string;
  placeholderName: string;
  heading: string;
  onCancel: () => void;
  onSave: (workflowTemplateId: string) => void;
  // optional params
  startsEditable?: boolean;
  existingWorkflowId?: string; // FR & deliverables enforces that there can only be one workflowId per org
  existingWorkflowGraph?: WorkflowGraphObjectGQLType;
}) => {
  const toast = useToast();

  const {
    user: { id: authorUserId },
  } = useUserAuthOrThrow();

  const { data: workflowTemplateData } = useGetWorkflowTemplateConstructorQuery(
    {
      fetchPolicy: 'network-only',
      variables: { workflowType, organizationId },
    },
  );

  const [errors, setErrors] = useState<string[]>([]);

  const [
    createWorkflowTemplateMutation,
    {
      data: createWorkflowTemplateData,
      loading: createWorkflowTemplateLoading,
    },
  ] = useCreateWorkflowTemplateMutation({
    fetchPolicy: 'network-only',
    onError: () => {
      toast({
        title: `${heading}: Error. Please contact Finley support.`,
        status: 'error',
        position: 'top',
      });
    },
  });

  useEffect(() => {
    if (createWorkflowTemplateData) {
      const result = createWorkflowTemplateData.createWorkflowTemplate;

      if (result.__typename) {
        switch (result.__typename) {
          case 'SuccessCreateWorkflowTemplateResponse': {
            toast({
              title: `${heading}: Success`,
              duration: 2000,
              status: 'success',
              position: 'top',
            });
            onSave(result.template.id);
            break;
          }
          case 'ErrorCreateWorkflowTemplateResponse': {
            setErrors(result.errors);
            break;
          }
          default:
            finleyAssertUnreachable(result.__typename);
        }
      }
    }
  }, [createWorkflowTemplateData, heading, onSave, toast]);

  const [workflowTemplateName, setWorkflowTemplateName] = useState(
    placeholderName,
  );

  const initialRows = useMemo(
    () =>
      existingWorkflowGraph
        ? constructWorkflowGraphNodesList(existingWorkflowGraph)
        : [],
    [existingWorkflowGraph],
  );

  const [userApprovalNodes, setUserApprovalNodes] = useState(initialRows);

  const isInitialGraph = useMemo(() => {
    // first check if the graph is the same as "initial graph"
    return _.every(userApprovalNodes, (node, nodeIndex) => {
      const initialRowIndex = initialRows.findIndex((row) => row === node);
      return initialRowIndex === nodeIndex;
    });
  }, [initialRows, userApprovalNodes]);

  const [editMode, setEditMode] = useState<boolean>(startsEditable);

  // drag and drop (move into generic component)
  function onDragEnd(result: DropResult) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    setUserApprovalNodes((prev) => {
      if (!result.destination) {
        return prev;
      }

      return reorder(prev, result.source.index, result.destination.index);
    });
  }

  const onClick = useCallback(
    async (workflowTemplate: WorkflowTemplateConstructorGQLType) => {
      const edges = constructWorkflowGraphEdges(userApprovalNodes);

      const finalActionNode: WorkflowActionGraphNodeGQLType | null = workflowTemplate.finalAction
        ? {
            id: uuidv4(),
            actionFunctionName: workflowTemplate.finalAction.functionName,
            actionParams: [],
          }
        : null;

      if (finalActionNode) {
        // connect last approval to final action, if exists
        edges.push({
          from: userApprovalNodes[userApprovalNodes.length - 1].id,
          to: finalActionNode.id,
        });
      }
      await createWorkflowTemplateMutation({
        variables: {
          workflowType,
          workflowId: existingWorkflowId ?? uuidv4(),
          workflow: {
            userApprovalNodes,
            actionNodes: finalActionNode ? [finalActionNode] : [],
            edges,
          },
          organizationId,
          authorUserId,
          name: workflowTemplateName,
        },
      });
      setEditMode(false);
    },
    [
      authorUserId,
      createWorkflowTemplateMutation,
      existingWorkflowId,
      organizationId,
      userApprovalNodes,
      workflowTemplateName,
      workflowType,
    ],
  );

  if (!workflowTemplateData) {
    return <Skeleton height="400px" />;
  }

  const workflowTemplate = workflowTemplateData.getWorkflowTemplateConstructor;

  const approvalUserOptions =
    workflowTemplate.optionsExposedToUsers.domainSpecificRuntimeVariable
      ?.usersApprovalRuntimeVariable.options;

  finleyAssert(
    approvalUserOptions,
    `[Workflows] Cannot author template without usersApprovalRuntimeVariable exposed in template constructor.`,
  );

  return (
    <Stack gap="1rem" width="full">
      <HStack mb="2rem">
        <Typography variant="section-title" trim>
          {heading}
        </Typography>
        <Spacer />

        {editMode ? (
          <Flex gap="0.5rem">
            <Button
              size="sm"
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={() => {
                setEditMode(false);
                setUserApprovalNodes(initialRows);
                onCancel();
              }}
            >
              Cancel
            </Button>

            <Button
              size="sm"
              variant="primary-muted"
              isActive
              colorScheme="v2-aurora"
              isLoading={createWorkflowTemplateLoading}
              isDisabled={
                isInitialGraph ||
                userApprovalNodes.length === 0 ||
                userApprovalNodes.some((n) => n.userIds.length === 0)
              }
              onClick={async () => {
                await onClick(workflowTemplate);
              }}
            >
              Save
            </Button>
          </Flex>
        ) : (
          <Button
            size="sm"
            variant="primary-muted"
            colorScheme="v2-aurora"
            onClick={() => {
              setEditMode(true);
            }}
          >
            Edit
          </Button>
        )}
      </HStack>

      <Stack gap="2rem">
        <Stack gap="1em">
          <Typography fontWeight="bold">Name</Typography>
          <Input
            value={workflowTemplateName}
            isDisabled={!editMode}
            onChange={(event) => {
              setWorkflowTemplateName(event.target.value);
            }}
          />
        </Stack>

        <Stepper
          index={-1} // mock show all steps as gray
          orientation="vertical"
          colorScheme="green"
          width="full"
          gap={0}
        >
          {/* title step (e.g. what triggers the workflow) */}
          <Box width="full">
            <Step key={workflowTemplate.title}>
              <StepSeparator />

              <StepIndicator>
                <StepStatus
                  complete={<Icon icon="check" />}
                  incomplete={<Icon icon="check" />}
                  active={<Icon icon="check" />}
                />
              </StepIndicator>

              <Box width="full" height={editMode ? '6em' : '4em'} mt="4px">
                <Typography
                  variant="section-subtitle"
                  fontWeight="semibold"
                  trim
                >
                  {commonUtils.toTitleCase(workflowTemplate.title)}
                </Typography>
                {editMode && (
                  <Button
                    my={4}
                    variant="secondary"
                    colorScheme="v2-aurora"
                    leftIcon={<Icon icon="plus" />}
                    size="sm"
                    onClick={() => {
                      setUserApprovalNodes((curr) => [
                        ...curr,
                        {
                          id: uuidv4(),
                          comparator: 'CONTAINS_ANY_OF',
                          userIds: [],
                        },
                      ]);
                    }}
                  >
                    Add
                  </Button>
                )}
              </Box>
            </Step>
          </Box>

          {/* configurable nodes */}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable
              droppableId="workflowNodesOrdering"
              direction="vertical"
              renderClone={(provided, snapshot, rubric) => {
                const node = userApprovalNodes.find(
                  ({ id }) => id === rubric.draggableId,
                );
                finleyAssert(
                  node,
                  `Expected comparisonKey [${rubric.draggableId}] to exist in node ids`,
                );

                return (
                  <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={{ w: '100%' }}
                  >
                    <WorkflowUserApprovalGraphNode
                      node={node}
                      isDragging={snapshot.isDragging}
                      isEditable={false}
                      approvalUserOptions={approvalUserOptions.map(
                        ({ label, stringValue: value }) => ({
                          label,
                          value,
                        }),
                      )}
                    />
                  </Box>
                );
              }}
            >
              {(provided) => (
                <Box width="full">
                  <Stack
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    spacing="0"
                  >
                    {userApprovalNodes.map((node, index) =>
                      !editMode ? (
                        <Step key={JSON.stringify(node)}>
                          <StepSeparator />

                          <StepIndicator>
                            <StepStatus
                              complete={<Icon icon="check" />}
                              incomplete={<Icon icon="check" />}
                              active={<Icon icon="check" />}
                            />
                          </StepIndicator>

                          <Box
                            sx={{
                              w: '100%',
                              mt: '4px',
                              height: '7em',
                            }}
                          >
                            <WorkflowUserApprovalGraphNode
                              node={node}
                              isDragging={false}
                              isEditable={false}
                              approvalUserOptions={approvalUserOptions.map(
                                ({ label, stringValue: value }) => ({
                                  label,
                                  value,
                                }),
                              )}
                            />
                          </Box>
                        </Step>
                      ) : (
                        <Draggable
                          key={node.id}
                          draggableId={node.id}
                          index={index}
                        >
                          {(draggableProvided, draggableSnapshot) => {
                            return (
                              <Step key={JSON.stringify(node)}>
                                <StepSeparator />

                                <StepIndicator>
                                  <StepStatus
                                    complete={<Icon icon="check" />}
                                    incomplete={<Icon icon="check" />}
                                    active={<Icon icon="check" />}
                                  />
                                </StepIndicator>

                                <Box
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  sx={{
                                    w: '100%',
                                    mt: '4px',
                                    height: '7em',
                                  }}
                                >
                                  <WorkflowUserApprovalGraphNode
                                    node={node}
                                    isEditable
                                    isDragging={draggableSnapshot.isDragging}
                                    approvalUserOptions={approvalUserOptions.map(
                                      ({ label, stringValue: value }) => ({
                                        label,
                                        value,
                                      }),
                                    )}
                                    onDelete={() => {
                                      setUserApprovalNodes((curr) => {
                                        const currCopy = [...curr];
                                        currCopy.splice(index, 1);

                                        return currCopy;
                                      });
                                    }}
                                    onChange={(newNode) => {
                                      setUserApprovalNodes((curr) => {
                                        const currCopy = [...curr];
                                        currCopy[index] = newNode;

                                        return currCopy;
                                      });
                                    }}
                                  />
                                </Box>
                              </Step>
                            );
                          }}
                        </Draggable>
                      ),
                    )}
                    {provided.placeholder}
                  </Stack>
                </Box>
              )}
            </Droppable>
          </DragDropContext>

          {/* final action if it exists */}
          {workflowTemplate.finalAction && (
            <Box width="full">
              <Step key={workflowTemplate.finalAction.title}>
                <StepSeparator />

                <StepIndicator>
                  <StepStatus
                    complete={<Icon icon="check" />}
                    incomplete={<Icon icon="check" />}
                    active={<Icon icon="check" />}
                  />
                </StepIndicator>

                <Box width="full" mt="2px">
                  <Typography variant="section-subtitle" fontWeight="semibold">
                    {commonUtils.toTitleCase(
                      workflowTemplate.finalAction.title,
                    )}
                  </Typography>
                </Box>
              </Step>
            </Box>
          )}
        </Stepper>
      </Stack>
      {errors.length > 0 && (
        <Box padding="16px" borderRadius="8px" maxH="300px" overflowY="auto">
          <Typography fontWeight="bold">
            Failed to save. Address the following errors:
          </Typography>
          <UnorderedList>
            {errors.map((error) => {
              return (
                <ListItem fontSize="small" key={error}>
                  {error}
                </ListItem>
              );
            })}
          </UnorderedList>
        </Box>
      )}
    </Stack>
  );
};

export const WorkflowTemplate = ({
  workflowTemplateId,
  heading,
  onCancel,
  onSave,
}: {
  workflowTemplateId: string;
  heading: string;
  onCancel: () => void;
  onSave: (workflowTemplateId: string) => void;
}) => {
  const workflowTemplateQuery = useGetWorkflowTemplateQuery({
    fetchPolicy: 'network-only',
    variables: { workflowTemplateId },
  });

  if (workflowTemplateQuery.error) {
    Sentry.captureException(workflowTemplateQuery.error);
    throw new Error(`${heading}: Error. Please contact Finley support.`);
  }

  if (!workflowTemplateQuery.data) {
    return <Skeleton height="400px" />;
  }

  const workflowTemplate = workflowTemplateQuery.data.getWorkflowTemplate;

  return (
    <WorkflowAuthorTemplate
      workflowType={workflowTemplate.workflowType}
      organizationId={workflowTemplate.organizationId}
      placeholderName={workflowTemplate.name}
      heading={heading}
      onCancel={onCancel}
      onSave={onSave}
      existingWorkflowId={workflowTemplate.workflowId}
      existingWorkflowGraph={workflowTemplate.workflow}
    />
  );
};

export const WorkflowNewTemplate = ({
  workflowType,
  placeholderName,
  heading,
  onCancel,
  onSave,
  canMultipleTemplatesExist,
}: {
  workflowType: WorkflowTypeGQLType;
  placeholderName: string;
  heading: string;
  onCancel: () => void;
  onSave: (workflowTemplateId: string) => void;
  canMultipleTemplatesExist: boolean;
}) => {
  const {
    user: { organizationId },
  } = useUserAuthOrThrow();

  const existingWorkflowTemplatesQuery = useGetWorkflowTemplatesQuery({
    variables: {
      workflowType,
      organizationId,
    },
  });

  if (existingWorkflowTemplatesQuery.error) {
    Sentry.captureException(existingWorkflowTemplatesQuery.error);
    throw new Error(
      `Failed to get Workflow Templates for ${workflowType}. Please contact Finley support.`,
    );
  }

  // if canMultipleTemplatesExist === false, check to ensure that no workflow templates exist for this org + workflowType
  if (!existingWorkflowTemplatesQuery.data) {
    return <Skeleton h="400px" />;
  }

  const existingWorkflowTemplatesCount =
    existingWorkflowTemplatesQuery.data.getWorkflowTemplates.length;
  if (
    canMultipleTemplatesExist === false &&
    existingWorkflowTemplatesCount > 0
  ) {
    return (
      <Typography>
        Cannot create new template as other templates already exist.
      </Typography>
    );
  }

  return (
    <WorkflowAuthorTemplate
      workflowType={workflowType}
      organizationId={organizationId}
      placeholderName={placeholderName}
      heading={heading}
      onCancel={onCancel}
      onSave={onSave}
      startsEditable
    />
  );
};
